/*
 * BulkImport API Service
 */

import BaseApiInstance from '../../api/base';

/**
 * File Upload API
 */
export const fileUploadAPI = {
  // Initialize progressive file upload
  initializeUpload: async ({ fileName, fileSize, mimeType, totalChunks }) => {
    const response = await BaseApiInstance.post('/file-upload/initialize', {
      fileName,
      fileSize,
      mimeType,
      totalChunks,
      context: 'bulk_import',
    });
    return response.data;
  },

  // Upload file chunk
  uploadChunk: async ({
    uploadId,
    chunkIndex,
    totalChunks,
    chunkSize,
    chunkData,
  }) => {
    const response = await BaseApiInstance.post('/file-upload/upload-chunk', {
      uploadId,
      chunkIndex,
      totalChunks,
      chunkSize,
      chunkData, // base64 encoded chunk
    });
    return response.data;
  },

  // Finalize file upload
  finalizeUpload: async ({ uploadId, metadata = {} }) => {
    const response = await BaseApiInstance.post('/file-upload/finalize', {
      uploadId,
      context: 'bulk_import',
      metadata,
    });
    return response.data;
  },

  // Get upload status
  getUploadStatus: async uploadId => {
    const response = await BaseApiInstance.get(
      `/file-upload/status/${uploadId}`,
    );
    return response.data;
  },
};

/**
 * Bulk Import API
 */
export const bulkImportAPI = {
  // Get required fields for import type
  getRequiredFields: async importType => {
    const response = await BaseApiInstance.get(
      `/bulk-import/required-fields/${importType}`,
    );
    return response.data;
  },

  // Analyze uploaded file
  analyzeFile: async ({ fileId, importType }) => {
    const response = await BaseApiInstance.post(
      `/bulk-import/analyze-file/${fileId}`,
      {
        importType,
      },
    );
    return response.data;
  },

  // Create import job
  createJob: async ({ fileId, importType, columnMapping }) => {
    const response = await BaseApiInstance.post('/bulk-import/create-job', {
      fileId,
      importType,
      columnMapping,
    });
    return response.data;
  },

  // Get import jobs
  getJobs: async (limit = 10) => {
    const response = await BaseApiInstance.get(
      `/bulk-import/jobs?limit=${limit}`,
    );
    return response.data;
  },

  // Get specific job
  getJob: async jobId => {
    const response = await BaseApiInstance.get(`/bulk-import/jobs/${jobId}`);
    return response.data;
  },

  // Start processing an import job
  startJob: async jobId => {
    const response = await BaseApiInstance.post(
      `/bulk-import/start-job/${jobId}`,
    );
    return response.data;
  },
};

/**
 * File processing utilities
 */
export const fileUtils = {
  // Convert file to base64 chunks
  fileToChunks: (file, chunkSize = 1024 * 1024) =>
    // 1MB chunks by default
    new Promise((resolve, reject) => {
      const chunks = [];
      const totalChunks = Math.ceil(file.size / chunkSize);
      let currentChunk = 0;

      const processChunk = () => {
        if (currentChunk >= totalChunks) {
          resolve({
            chunks,
            totalChunks,
            fileName: file.name,
            fileSize: file.size,
            mimeType: file.type,
          });
          return;
        }

        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);

        const reader = new FileReader();
        reader.onload = e => {
          const base64Data = e.target.result.split(',')[1]; // Remove data:... prefix
          chunks.push({
            index: currentChunk,
            size: chunk.size,
            data: base64Data,
          });
          currentChunk += 1;
          processChunk();
        };
        reader.onerror = () => reject(new Error('Failed to read file chunk'));
        reader.readAsDataURL(chunk);
      };

      processChunk();
    }),
  // Validate file type for bulk import
  validateFile: file => {
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
    ];

    const allowedExtensions = ['.xlsx', '.xls'];

    const isValidType = allowedTypes.includes(file.type);
    const isValidExtension = allowedExtensions.some(ext =>
      file.name.toLowerCase().endsWith(ext),
    );

    if (!isValidType && !isValidExtension) {
      throw new Error(
        'Invalid file type. Please upload an Excel file (.xlsx or .xls).',
      );
    }

    // Check file size (max 50MB)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      throw new Error('File size exceeds 50MB limit.');
    }

    return true;
  },
};
